import { useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  ErrorBanner,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@lumanity/ui/primitives";
import OutletGuidelinesForm from "../OutletGuidelinesForm";
import { useAdminOutlets, useOutletMutations } from "../outputsApi";
import { OUTLET_KIND_LABELS, emptyGuidelines, formatLimit, normalizeGuidelines } from "../model";
import type { Outlet, OutletInput, OutletKind } from "../types";

const emptyOutlet = (): OutletInput => ({ kind: "journal", name: "", publisher: null, is_active: true, guidelines: emptyGuidelines() });

const toInput = (o: Outlet): OutletInput => ({
  kind: o.kind,
  name: o.name,
  publisher: o.publisher,
  is_active: o.is_active,
  guidelines: normalizeGuidelines(o.guidelines),
});

/** Journals and congresses writers can target, with their submission guidelines (FR-4-03). */
const TargetOutletsAdmin = () => {
  const { data: outlets = [], isLoading, error } = useAdminOutlets();
  const { create, update, remove } = useOutletMutations();
  const [editing, setEditing] = useState<{ id: string | null; value: OutletInput } | null>(null);
  const [deleting, setDeleting] = useState<Outlet | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const openEditor = (outlet: Outlet | null) => {
    setDialogError(null);
    setEditing({ id: outlet?.id ?? null, value: outlet ? toInput(outlet) : emptyOutlet() });
  };

  const saving = create.isPending || update.isPending;
  const handleSave = async () => {
    if (!editing) return;
    const body: OutletInput = {
      ...editing.value,
      name: editing.value.name.trim(),
      publisher: editing.value.publisher?.trim() || null,
    };
    try {
      if (editing.id) await update.mutateAsync({ id: editing.id, body });
      else await create.mutateAsync(body);
      toast({ title: editing.id ? "Outlet updated" : "Outlet added" });
      setEditing(null);
    } catch (e) {
      setDialogError((e as Error).message);
    }
  };

  const handleDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => toast({ title: `${deleting.name} deleted` }),
      onError: (e) => toast({ title: "Could not delete the outlet", description: e.message, variant: "destructive" }),
      onSettled: () => setDeleting(null),
    });
  };

  const setValue = (patch: Partial<OutletInput>) => setEditing((e) => (e ? { ...e, value: { ...e.value, ...patch } } : e));

  if (error) return <ErrorBanner message={`Could not load outlets: ${error.message}`} />;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => openEditor(null)}>
          <Plus className="w-4 h-4 mr-1" /> Add outlet
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead className="w-28">Kind</TableHead>
            <TableHead>Publisher</TableHead>
            <TableHead>Word/character limit</TableHead>
            <TableHead className="w-24">Status</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                Loading outlets...
              </TableCell>
            </TableRow>
          ) : outlets.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                No outlets yet.
              </TableCell>
            </TableRow>
          ) : (
            outlets.map((o) => (
              <TableRow key={o.id}>
                <TableCell className="font-medium">{o.name}</TableCell>
                <TableCell>{OUTLET_KIND_LABELS[o.kind] ?? o.kind}</TableCell>
                <TableCell className="text-muted-foreground">{o.publisher ?? "—"}</TableCell>
                <TableCell className="text-muted-foreground">{formatLimit(o.guidelines?.body_limit ?? null)}</TableCell>
                <TableCell>
                  <Badge variant={o.is_active ? "default" : "secondary"}>{o.is_active ? "Active" : "Inactive"}</Badge>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditor(o)} aria-label={`Edit ${o.name}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => setDeleting(o)}
                    aria-label={`Delete ${o.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={!!editing} onOpenChange={(open) => !open && !saving && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit outlet" : "Add outlet"}</DialogTitle>
            <DialogDescription>Writers start from these guidelines when they pick the outlet, and can adjust them per output.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="outlet-name">Name</Label>
                  <Input id="outlet-name" value={editing.value.name} onChange={(e) => setValue({ name: e.target.value })} disabled={saving} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="outlet-publisher">Publisher</Label>
                  <Input
                    id="outlet-publisher"
                    value={editing.value.publisher ?? ""}
                    onChange={(e) => setValue({ publisher: e.target.value })}
                    disabled={saving}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="outlet-kind">Kind</Label>
                  <Select value={editing.value.kind} onValueChange={(v) => setValue({ kind: v as OutletKind })} disabled={saving}>
                    <SelectTrigger id="outlet-kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(OUTLET_KIND_LABELS) as OutletKind[]).map((k) => (
                        <SelectItem key={k} value={k}>
                          {OUTLET_KIND_LABELS[k]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 self-end pb-2 text-sm">
                  <Checkbox
                    checked={editing.value.is_active}
                    onCheckedChange={(c) => setValue({ is_active: c === true })}
                    disabled={saving}
                  />
                  Active (offered to writers)
                </label>
              </div>

              <section className="space-y-3 border-t pt-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-brand-navy">Guidelines</h3>
                <OutletGuidelinesForm
                  value={editing.value.guidelines}
                  onChange={(guidelines) => setValue({ guidelines })}
                  disabled={saving}
                  idPrefix="admin-outlet"
                />
              </section>

              {dialogError && <ErrorBanner message={dialogError} />}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSave()} disabled={saving || !editing?.value.name.trim()}>
              {editing?.id ? "Save" : "Add outlet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Outputs that already target it keep their copy of its guidelines. To stop offering it without deleting, mark it inactive instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TargetOutletsAdmin;
